// src/components/UploadedFileRow.tsx

import {
  getFileTypeName,
  IconWrapper,
  IStyles,
  pxToRem,
  pxTovW,
  ScoreProgressBarLinear,
} from '@geneo2-web/shared-ui';
import { Box, Typography } from '@mui/material';
import { CustomUploadFileResponse } from '../../reducer/assessmentFlow.slice';

const styles: IStyles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: { xs: pxToRem(10), md: pxTovW(10) },
    width: '100%',
    height: { xs: pxToRem(100), md: pxTovW(117) },
    backgroundColor: '#F3FAFF',
    border: '1px dashed #B6E1FF',
    borderRadius: pxToRem(6),
    px: pxToRem(12),
    py: pxToRem(10),
  },

  fileNameSizeBox: {
    borderRadius: pxToRem(6),
    backgroundColor: '#E6F6ED',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  percentageAndIconBox: {
    display: 'flex',
    alignItems: 'center',
    gap: pxToRem(6),
  },

  fileMetaColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(10), md: pxTovW(20) },
  },

  percentageText: {
    fontSize: pxToRem(12),
    fontWeight: 500,
    color: '#2E7D32',
  },

  errorText: {
    fontSize: pxToRem(12),
    color: '#D32F2F',
  },

  progressBar: {
    height: pxToRem(6),
    borderRadius: pxToRem(6),
  },

  deleteFileHandler: {
    cursor: 'pointer',
    ml: pxToRem(6),
  },
};
interface UploadedFileRowProps {
  file: CustomUploadFileResponse;
  progress: number;
  onDelete?: () => void;
}

export const UploadedFile = ({
  file,
  progress,
  onDelete,
}: UploadedFileRowProps) => {
  const isPending = file?.fileUploadStatus === 'pending';
  const isSuccess = file?.fileUploadStatus === 'success';

  const percentage = isSuccess ? 100 : isPending ? progress : 0;

  const formatFileSize = (bytes: number): string => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
  };

  return (
    <Box sx={styles.root}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: pxToRem(12),
          width: '100%',
        }}
      >
        {/* Left: icon + meta */}
        <Box
          sx={{ display: 'flex', gap: pxToRem(10), flexGrow: 1, width: '100%' }}
        >
          <Box sx={styles.fileNameSizeBox}>
            <IconWrapper
              name="xls-file-green"
              size="large"
              parentFolder="icons"
              type="png"
              customSx={{
                width: { xs: pxToRem(41), md: pxTovW(61) },
                height: { xs: pxToRem(41), md: pxTovW(61) },
              }}
            />
          </Box>

          <Box sx={styles.fileMetaColumn}>
            <Typography variant="h3" fontWeight="bold">
              {file?.uploadFileData?.fileName ?? 'Uploaded file'}
            </Typography>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  width: '100%',
                  alignItems: 'center',
                  gap: { xs: pxToRem(5), md: pxTovW(10) },
                }}
              >
                <Typography variant="h4" fontWeight="bold" color="neutral.grey">
                  {formatFileSize(file?.fileSize)}
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="neutral.grey">
                  .
                </Typography>

                <Typography variant="h4" fontWeight="bold" color="neutral.grey">
                  {isSuccess ? 'Completed' : isPending ? `Pending` : 'Failed'}
                </Typography>
              </Box>
              <Box sx={styles.percentageAndIconBox}></Box>

              <Typography
                sx={styles.percentageText}
              >{`${progress}%`}</Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Progress bar */}
      <ScoreProgressBarLinear
        score={percentage}
        variant="small"
        customSx={{
          mt: pxToRem(8),
          ...styles.progressBar,
          backgroundColor: '#CBF0DC',
        }}
      />
    </Box>
  );
};
